import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { MapPin, Phone, Mail } from "lucide-react"
import { apiService } from "@/services/api"

interface ContactInfo {
  phone?: string
  email?: string
  addressZhHk?: string
  addressZhCn?: string
  addressEn?: string
  isPhoneVisible?: boolean
  isEmailVisible?: boolean
  isAddressVisible?: boolean
}

export function ContactSection() {
  const { t, i18n } = useTranslation()
  const [info, setInfo] = useState<ContactInfo>({})

  useEffect(() => {
    apiService.contactInfo.get()
      .then(res => { if (res.data) setInfo(res.data) })
      .catch(() => {})
  }, [])

  const getAddress = () => {
    const lang = i18n.language
    if (lang === 'zh-TW' || lang === 'zh-HK') return info.addressZhHk
    if (lang === 'zh-CN') return info.addressZhCn
    return info.addressEn || info.addressZhHk
  }

  const phone = info.phone || t('home.contact.phone')
  const email = info.email || t('home.contact.email')
  const address = getAddress() || t('home.contact.address')

  return (
    <section id="contact" className="bg-muted/50 py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">{t('home.contact.title')}</h2>
            <p className="mt-3 text-muted-foreground">
              {t('home.contact.subtitle')}
            </p>

            <div className="mt-8 space-y-6">
              {info.isAddressVisible !== false && (
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t('home.contact.addressTitle')}</p>
                    <p className="text-sm text-muted-foreground">{address}</p>
                  </div>
                </div>
              )}

              {info.isPhoneVisible !== false && (
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t('home.contact.phoneTitle')}</p>
                    <p className="text-sm text-muted-foreground">{phone}</p>
                  </div>
                </div>
              )}

              {info.isEmailVisible !== false && (
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t('home.contact.emailTitle')}</p>
                    <p className="text-sm text-muted-foreground">{email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Card>
            <CardContent className="p-6">
              <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground">
                      {t('home.contact.form.name')}
                    </label>
                    <Input id="name" placeholder={t('home.contact.form.namePlaceholder')} className="bg-background" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground">
                      {t('home.contact.form.email')}
                    </label>
                    <Input id="email" type="email" placeholder={t('home.contact.form.emailPlaceholder')} className="bg-background" />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground">
                    {t('home.contact.form.message')}
                  </label>
                  <Textarea
                    id="message"
                    placeholder={t('home.contact.form.messagePlaceholder')}
                    rows={4}
                    className="bg-background"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                  {t('home.contact.form.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
